import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { minisites } from '@/utils/api';
import { ArrowRight, Quote } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';

export default function MiniSite() {
  const { brandSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    minisites.publicGet(brandSlug).then(r => setData(r.data)).catch(() => setData({ error: true })).finally(() => setLoading(false));
  }, [brandSlug]);

  usePageMeta({
    title: data?.brand?.name ? `${data.brand.name} · ${data?.site?.tagline || 'We automate what matters.'}` : 'Our work',
    description: data?.site?.hero_md?.slice(0, 160) || 'See case studies and client testimonials.',
    image: data?.brand?.logo_url || undefined,
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (data?.error || !data) return (
    <div className="min-h-screen flex items-center justify-center text-center p-6">
      <p>This site is not currently available.</p>
    </div>
  );

  const { brand, site, testimonials = [], auto_case_studies = [] } = data;
  const primary = brand.primary_color || '#006080';
  const allCaseStudies = [
    ...(site.case_studies || []),
    ...auto_case_studies,
  ];

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", background: '#FAF8F4' }} data-testid="minisite-public">
      <header className="py-6 px-6 border-b" style={{ borderColor: '#E8E3D9' }}>
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.name} className="h-8" />
          ) : (
            <div className="w-8 h-8 rounded flex items-center justify-center font-bold text-white" style={{ background: primary }}>
              {brand.name?.charAt(0) || 'B'}
            </div>
          )}
          <p className="font-black text-sm">{brand.name}</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        {site.tagline && (
          <p className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: primary }}>{site.tagline}</p>
        )}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6" style={{ color: '#1A2428' }}>
          {brand.name}
        </h1>
        {site.hero_md && (
          <p className="text-base mb-8 whitespace-pre-wrap" style={{ color: '#5C6B72', maxWidth: 640, lineHeight: 1.7 }}>
            {site.hero_md}
          </p>
        )}
        {site.cta_url && (
          <a href={site.cta_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-btn font-bold text-white text-sm" style={{ background: primary }} data-testid="minisite-cta">
            {site.cta_text || 'Book a call'} <ArrowRight size={14} />
          </a>
        )}

        {allCaseStudies.length > 0 && (
          <section className="mt-16">
            <p className="text-xs uppercase tracking-wider font-bold mb-6" style={{ color: primary }}>Case Studies</p>
            <div className="grid md:grid-cols-2 gap-6">
              {allCaseStudies.map((cs, i) => (
                <div key={i} className="p-6 rounded-card bg-white border" style={{ borderColor: '#E8E3D9' }} data-testid={`case-${i}`}>
                  <h3 className="font-black text-lg mb-2" style={{ color: '#1A2428' }}>{cs.title}</h3>
                  {cs.summary_md && <p className="text-sm whitespace-pre-wrap mb-3" style={{ color: '#5C6B72' }}>{cs.summary_md}</p>}
                  {cs.result && (
                    <p className="text-sm font-bold" style={{ color: primary }}>→ {cs.result}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {testimonials.length > 0 && (
          <section className="mt-16" data-testid="minisite-testimonials">
            <p className="text-xs uppercase tracking-wider font-bold mb-6" style={{ color: primary }}>What clients say</p>
            <div className="grid md:grid-cols-2 gap-6">
              {testimonials.map((t, i) => (
                <figure key={i} className="p-6 rounded-card bg-white border relative" style={{ borderColor: '#E8E3D9' }} data-testid={`testimonial-${i}`}>
                  <Quote size={20} style={{ color: `${primary}60`, position: 'absolute', top: 16, right: 16 }} />
                  <blockquote className="text-sm mb-4 italic" style={{ color: '#1A2428', lineHeight: 1.6 }}>
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="flex items-center gap-2">
                    <span className="font-mono font-bold text-xs px-2 py-1 rounded" style={{ background: `${primary}15`, color: primary }}>
                      {t.score}/10
                    </span>
                    {t.project_name && <span className="text-xs" style={{ color: '#5C6B72' }}>on {t.project_name}</span>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        {site.contact_email && (
          <section className="mt-16 pt-8" style={{ borderTop: '1px solid #E8E3D9' }}>
            <p className="text-sm" style={{ color: '#5C6B72' }}>
              Get in touch: <a href={`mailto:${site.contact_email}`} style={{ color: primary, fontWeight: 700 }}>{site.contact_email}</a>
            </p>
          </section>
        )}
      </main>

      <footer className="text-center text-xs py-6" style={{ color: '#8A99A0' }}>
        © {new Date().getFullYear()} {brand.name}
      </footer>
    </div>
  );
}
